import { useRef, useState, ChangeEvent, useEffect, useCallback } from 'react';
import styles from './VerifyEmail.module.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { createLoginToken, initiateMFAEmail, verifyMFAEmail } from '../../auth/_requests';
import { postLoginTask } from '../../helpers/utils/login';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, RotateCw } from 'lucide-react';
import { AuthBrandedLoader } from '../Login/AuthBrandedLoader';
import loginStyles from '../Login/Login.module.css';
import { AuthPageShell } from '../Login/AuthPageShell';
import logoUrl from '../../assets/helpers/navbar/tenantpay-white.png';

const OTP_LENGTH = 4;
const CODE_EXPIRY_SECONDS = 900; // 15 minutes
const RESEND_COOLDOWN_SECONDS = 45;
const INCORRECT_CODE_MESSAGE = 'That code is incorrect. Check your email and try again.';

export function VerifyEmail() {
  const { auth, currentUser, logout, saveAuth } = useAuth();
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const [inputValues, setInputValues] = useState(['', '', '', '']);
  const [errorMessage, setErrorMessage] = useState('');
  const [validMessage, setValidMessage] = useState('');
  const codeSentRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(CODE_EXPIRY_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const verifyingRef = useRef(false);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const clearOtpInputs = () => {
    setInputValues(['', '', '', '']);
    inputRefs[0].current?.focus();
  };

  const initiateMFASession = async () => {
    try {
      setLoading(true);
      await initiateMFAEmail(auth?.data.token || '');
      codeSentRef.current = true;
      setSecondsLeft(CODE_EXPIRY_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setErrorMessage('');
      clearOtpInputs();
    } catch (error) {
      console.error('Error sending code:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  const handleVerify = useCallback(
    async (codeOverride?: string) => {
      if (verifyingRef.current || isEmailVerified) return;

      const code = codeOverride ?? inputValues.join('');
      if (code.length !== OTP_LENGTH || !/^\d{4}$/.test(code)) {
        return;
      }

      setErrorMessage('');
      setLoadingVerify(true);
      verifyingRef.current = true;
      try {
        const token = auth?.data.token || '';
        const response = await verifyMFAEmail(token, code);
        if (response.message === 'verification_successful') {
          setValidMessage('Your email has been verified');
          setIsEmailVerified(true);
          try {
            const tokenResponse = await createLoginToken(auth?.data.token || '');
            const loginToken = tokenResponse.data.loginToken;
            const newAuth = {
              message: 'success',
              data: {
                tenantId: auth?.data.tenantId || '',
                token: loginToken,
              },
            };
            saveAuth(newAuth);
            setTimeout(() => {
              postLoginTask(newAuth, navigate, saveAuth);
            }, 2000);
          } catch (error) {
            console.error('Error creating login token:', error);
          }
        } else {
          setErrorMessage(INCORRECT_CODE_MESSAGE);
          setInputValues(['', '', '', '']);
          inputRefs[0].current?.focus();
        }
      } catch {
        setErrorMessage(INCORRECT_CODE_MESSAGE);
        setInputValues(['', '', '', '']);
        inputRefs[0].current?.focus();
      } finally {
        setLoadingVerify(false);
        verifyingRef.current = false;
      }
    },
    [auth?.data.token, auth?.data.tenantId, inputValues, isEmailVerified, navigate, saveAuth]
  );

  const handleInputChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.slice(0, 1);
    if (!/^\d$/.test(value)) {
      value = '';
    }
    if (errorMessage) {
      setErrorMessage('');
    }
    const newValues = [...inputValues];
    newValues[index] = value;
    setInputValues(newValues);
    if (value.length === 1 && index < OTP_LENGTH - 1) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleChangeEmail = () => {
    logout();
    navigate('/sign-up');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (['+', '-'].includes(e.key)) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter') {
      handleVerify();
      return;
    }
    if (e.key !== 'Backspace') return;

    e.preventDefault();
    if (errorMessage) {
      setErrorMessage('');
    }

    const newValues = [...inputValues];
    if (newValues[index]) {
      newValues[index] = '';
      setInputValues(newValues);
      return;
    }
    if (index > 0) {
      newValues[index - 1] = '';
      setInputValues(newValues);
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text');
    const digits = pastedData.replace(/\D/g, '').substring(0, OTP_LENGTH).split('');
    if (digits.length === 0) return;

    if (errorMessage) {
      setErrorMessage('');
    }

    const newValues = ['', '', '', ''];
    digits.forEach((digit, i) => {
      newValues[i] = digit;
    });
    setInputValues(newValues);
    inputRefs[Math.min(digits.length, OTP_LENGTH) - 1].current?.focus();

    if (digits.length === OTP_LENGTH) {
      void handleVerify(newValues.join(''));
    }
  };

  useEffect(() => {
    const sendInitialCode = async () => {
      try {
        setLoading(true);
        await initiateMFAEmail(auth?.data.token || '');
        codeSentRef.current = true;
        setSecondsLeft(CODE_EXPIRY_SECONDS);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (error) {
        console.error('Error sending code:', error);
      } finally {
        setLoading(false);
      }
    };
    void sendInitialCode();
  }, []);

  const canResend = resendCooldown <= 0;

  if (loading) {
    return <AuthBrandedLoader message='Loading…' />;
  }
  return (
    <AuthPageShell variant='center'>
      <div className={loginStyles.authFlowMax}>
        <motion.main
          className={`${loginStyles.main} ${loginStyles.mainWide} ${styles.verifyFlow}`}
          initial={false}
        >
          <div className={loginStyles.logoWrap}>
            <img src={logoUrl} className={loginStyles.logo} alt='logo' decoding='async' />
          </div>
          <button
            type='button'
            className={styles.headerOuter}
            onClick={() => {
              logout();
              navigate('/sign-up');
            }}
          >
            <ChevronLeftIcon className='text-white/70' stroke='currentColor' />
            <p className={styles.emailHeader}>Email verification</p>
          </button>
          <div className={styles.emailInner}>
            <p className={styles.emailText}>
              A verification code has been sent to{' '}
              <span className='font-bold'>
                {currentUser?.user?.email ? currentUser.user.email : ''}
              </span>
            </p>
            <button
              type='button'
              className={styles.changeEmailLink}
              onClick={handleChangeEmail}
            >
             Not your email? Edit
            </button>
            <p className={styles.emailText}>
              Please check your inbox and enter the verification code below to verify your email
              address.
            </p>
            <p className={styles.helperText}>Didn&apos;t get it? Check your spam or junk folder.</p>
            <div className={styles.codeBox}>
              <div className={styles.codeBoxInner}>
                <div className={styles.inputBoxes}>
                  {inputRefs.map((ref, index) => (
                    <input
                      key={index}
                      type='number'
                      className={clsx(styles.numberBox, errorMessage && styles.numberBoxError)}
                      ref={ref}
                      value={inputValues[index]}
                      onChange={handleInputChange(index)}
                      maxLength={1}
                      min={0}
                      max={9}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      aria-invalid={Boolean(errorMessage)}
                      inputMode='numeric'
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    />
                  ))}
                </div>
                {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
                {validMessage && <div className={`${styles.validMessage}`}>{validMessage}</div>}
              </div>
            </div>
            {codeSentRef.current === true && (
              <div className={styles.sendOuter}>
                {secondsLeft > 0 ? (
                  <p
                    className={`${styles.emailText} ${styles.timerText} ${
                      secondsLeft <= 30 ? styles.warning : ''
                    }`}
                  >
                    The code will expire in{' '}
                    <span className='font-bold'>{formatTime(secondsLeft)}</span>
                  </p>
                ) : (
                  <p className={styles.emailText}>
                    Code expired. Please click below to resend a new one.
                  </p>
                )}
                <button
                  type='button'
                  className={`${styles.resendCode} ${!canResend && styles.disabledResend}`}
                  onClick={canResend ? initiateMFASession : undefined}
                  disabled={!canResend}
                >
                  <RotateCw />
                  <p>{canResend ? 'Resend code' : `Resend code (${resendCooldown}s)`}</p>
                </button>
              </div>
            )}
          </div>
          <button
            type='button'
            className={clsx(
              loginStyles.submitBtn,
              (isEmailVerified || inputValues.some((val) => val === '') || loadingVerify) &&
                loginStyles.disableBtn
            )}
            onClick={() => handleVerify()}
            disabled={isEmailVerified || inputValues.some((val) => val === '') || loadingVerify}
          >
            {!loadingVerify && <span className='indicator-label'>Next</span>}
            {loadingVerify && (
              <span
                className={styles.indicatorProgress}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Please wait...
                <span className={styles.spinner}></span>
              </span>
            )}
          </button>
        </motion.main>
      </div>
    </AuthPageShell>
  );
}
